import cn from 'classnames'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { THEME } from 'src/config/config'
import { useSolutionsForVendorLazyQuery } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { AppState } from 'src/store/types'
import {
  isAlspSolution,
  isConsolidationProductSolution,
  isConsultancySolution,
  isGraveyardProductSolution,
  isProductSolution,
  SolutionDataFragment,
} from 'src/utils/SolutionDataFragment'

import { ClaimYourListingModal } from './ClaimYourListingModal'

interface ClaimYourListingProps {
  solution: SolutionDataFragment
}

export const ClaimYourListing: React.FC<ClaimYourListingProps> = ({ solution }) => {
  const router = useRouter()
  const { isVendor, status } = useAuthenticated()
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const [showModal, setShowModal] = useState(false)
  const [loadSolutions, { data: solutionsData }] = useSolutionsForVendorLazyQuery()
  const [hasProcessedClick, setHasProcessedClick] = useState(false)
  const [requestedSlug, setRequestedSlug] = useState<string | null>(null)

  // Get vendor name using the same logic as SolutionTitle (whatever comes after "by")
  const name = isAlspSolution(solution)
    ? solution?.alspData?.name
    : isConsultancySolution(solution)
      ? solution?.consultantData?.name
      : isProductSolution(solution)
        ? solution?.productData?.name
        : null

  const company = solution?.company || null
  const shouldShowCompany = company?.slug && company?.name
  const serviceName = isAlspSolution(solution)
    ? solution?.alspData?.serviceName
    : isConsultancySolution(solution)
      ? solution?.consultantData?.serviceName
      : isProductSolution(solution)
        ? solution?.productData?.tool
        : null

  // Vendor name is whatever comes after "by" in SolutionTitle
  // If shouldShowCompany is true, vendor name is company name (with fallback to name or serviceName or displayName)
  // Otherwise, vendor name is name (with fallback to serviceName or displayName)
  const displayVendorName = shouldShowCompany
    ? company?.name || name || serviceName || solution?.displayName || ''
    : name || serviceName || solution?.displayName || ''

  // 1. Display on all Solution Detail pages except: Graveyard solutions; Consolidation solutions where enableInActiveDir = false
  // Backend: enableInActiveDir defaults to false (solution-props/consolidation.json) and drives consolidationVisibility;
  // we hide when not explicitly true so we match backend and handle private/unset field as "disabled".
  // Applies to Product, ALSP, and Consultancy consolidation solutions.
  const isGraveyard = isGraveyardProductSolution(solution)
  const hasConsolidationData = !!solution?.consolidationData
  const isConsolidationSolution =
    isConsolidationProductSolution(solution) ||
    (isAlspSolution(solution) && hasConsolidationData) ||
    (isConsultancySolution(solution) && hasConsolidationData)
  const isConsolidationWithDisabledDir =
    isConsolidationSolution && solution?.consolidationData?.enableInActiveDir !== true
  const excludedBySolutionType = isGraveyard || isConsolidationWithDisabledDir

  // 2. Message is hidden for ILTA theme
  // 3. Visible to: logged-in vendor users; non-logged-in users. Hidden for logged-in non-vendor users
  const allowedByAuth = ((status === 'authenticated' && isVendor) || status === 'unauthenticated') && THEME !== 'ilta'

  // 4. Only display if solution is not already claimed. Claimed = vendor is published and has at least one user linked.
  const vendorData = solution?.vendor
  const vendorUsers = vendorData?.users || []
  const vendorPublishedAt = vendorData?.publishedAt
  const isClaimed = !!vendorPublishedAt && Array.isArray(vendorUsers) && vendorUsers.length > 0

  const shouldShow = !excludedBySolutionType && allowedByAuth && !isClaimed && !!displayVendorName

  // Handle the case when solutions data loads after clicking (slug-filtered request returns 0 or 1 solution)
  // Must be called unconditionally (before any early return) to satisfy Rules of Hooks
  React.useEffect(() => {
    if (!solutionsData || status !== 'authenticated' || !isVendor || !hasProcessedClick || showModal) return
    const currentSolutionSlug = solution?.slug
    if (!currentSolutionSlug || requestedSlug !== currentSolutionSlug) return // Only act when response is for this solution

    const total = solutionsData?.searchVendorSolutions?.total ?? 0
    const firstSolution = solutionsData?.searchVendorSolutions?.solutions?.[0]
    // When we have results, confirm they're for this slug (avoid stale cache from another page)
    if (total > 0 && firstSolution?.slug !== currentSolutionSlug) return

    const isSolutionAssociated = total > 0
    if (isSolutionAssociated) {
      if (router.isReady && (authInfo.name || authInfo.email) && authInfo.isVendor && authInfo.vendor) {
        setTimeout(() => {
          router.push('/my-solutions')
          setHasProcessedClick(false)
          setRequestedSlug(null)
        }, 200)
      }
    } else {
      setShowModal(true)
      setRequestedSlug(null)
    }
  }, [solutionsData, status, isVendor, router, showModal, hasProcessedClick, authInfo, solution?.slug, requestedSlug])

  if (!shouldShow) {
    return null
  }

  const handleAccessVendorPortal = async (e: React.MouseEvent) => {
    e.preventDefault()

    // Non-logged-in users: redirect to sign-in
    if (status === 'unauthenticated') {
      router.push('/signin')
      return
    }

    // Logged-in vendor users: check if this solution is already associated with the vendor
    if (status === 'authenticated' && isVendor) {
      const currentSolutionSlug = solution?.slug
      if (!currentSolutionSlug) {
        setShowModal(true)
        return
      }
      setHasProcessedClick(true)
      setRequestedSlug(currentSolutionSlug)
      // Backend supports slug filter: returns only this solution if it belongs to the vendor (scales to 4000+ solutions)
      await loadSolutions({
        variables: {
          query: {
            slug: currentSolutionSlug,
            offset: 0,
            limit: 1,
          },
        },
      })
      // Result is handled in useEffect when solutionsData updates
    }
  }

  return (
    <>
      <div className="mt-4 text-left pb-4 md:pb-0">
        <p
          className={cn('text-[1.125rem] font-primary font-regular text-darkgray', {
            '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
          })}
        >
          Do you manage profiles at <span className="font-semibold">{displayVendorName}</span>?{' '}
          <button
            onClick={handleAccessVendorPortal}
            className="text-[#011d58] underline cursor-pointer hover:text-[#3329EF]"
            type="button"
          >
            Access Vendor Portal
          </button>{' '}
          to manage this solution.
        </p>
      </div>
      {showModal && (
        <ClaimYourListingModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false)
            setHasProcessedClick(false)
            setRequestedSlug(null)
          }}
          vendorName={displayVendorName}
          solutionName={serviceName ? serviceName : name || ''}
          userEmail={authInfo.email || authInfo.username || ''}
        />
      )}
    </>
  )
}
